"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { MapPin, Calendar, Clock, Users, Car, CircleCheck, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ReservationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref: formRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const inputBaseClasses = "border-x-0 border-t-0 border-b border-border/30 focus-visible:border-primary focus-visible:ring-0 rounded-none bg-transparent px-0 shadow-none text-foreground placeholder:text-muted-foreground/30 transition-colors h-10 w-full outline-none font-default";
  if (isSubmitted && !submitError) {
    return <Card className="bg-card/80 backdrop-blur-2xl border-border/20 shadow-2xl w-full max-w-lg mx-auto lg:mx-0 lg:ml-auto animate-in fade-in zoom-in duration-500 rounded-lg">
        <CardContent className="pt-12 pb-12 flex flex-col items-center text-center space-y-6">
          <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center border border-primary/20">
            <CircleCheck className="size-8 text-primary" />
          </div>
          <div className="space-y-2">
            <h3 className="text-2xl font-default font-bold text-foreground tracking-tight">Transmission Received</h3>
            <p className="text-muted-foreground font-default">{successMessage}</p>
          </div>
          <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/30 hover:bg-primary/10 text-primary rounded-sm font-default">
            New Inquiry
          </Button>
        </CardContent>
      </Card>;
  }
  return <Card ref={formRef} className={`bg-card/40 backdrop-blur-2xl border-border/20 shadow-2xl w-full max-w-xl mx-auto lg:mx-0 lg:ml-auto transition-all duration-1000 transform rounded-lg ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`}>
      <CardHeader className="pb-4 border-b border-border/10">
        <CardTitle className="text-3xl font-default font-bold text-foreground tracking-tight uppercase">Initiate Integration</CardTitle>
        <CardDescription className="text-muted-foreground font-default">
          Connect with Tidal Swing AI to architect your next-generation intelligent ecosystem.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <Form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-8">
            
            {}
            <FormField control={control} name="full_name" rules={{
            required: "Name is required"
          }} render={({
            field
          }) => <FormItem className="col-span-1 md:col-span-2">
                  <FormLabel className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/70">Lead Architect / Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Alex Sterling" className={inputBaseClasses} {...field} />
                  </FormControl>
                  <FormDescription className="text-[10px] font-mono opacity-50">Primary point of contact for technical discovery.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="email" rules={{
            required: "Corporate email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/70">Corporate Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="architect@tidal-swing.ai" className={inputBaseClasses} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="phone" rules={{
            required: "Direct line is required",
            pattern: "phone"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/70">Direct Line</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+1 (555) 000-0000" className={inputBaseClasses} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="pickup_date" rules={{
            required: "Target date is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/70 flex items-center gap-2">
                    <SafeHydrate><Calendar className="size-3 text-primary" /></SafeHydrate> Target Launch
                  </FormLabel>
                  <FormControl>
                    <Input type="date" className={`${inputBaseClasses} [color-scheme:dark]`} {...field} />
                  </FormControl>
                  <FormDescription className="text-[10px] font-mono opacity-50">Projected go-live or milestone date.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="pickup_time" rules={{
            required: "Timeframe is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/70 flex items-center gap-2">
                    <Clock className="size-3 text-primary" /> Integration Window
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Q3 2024" className={inputBaseClasses} {...field} />
                  </FormControl>
                  <FormDescription className="text-[10px] font-mono opacity-50">Preferred development cycle.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="pickup_location" rules={{
            required: "Primary node is required"
          }} render={({
            field
          }) => <FormItem className="col-span-1 md:col-span-2">
                  <FormLabel className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/70 flex items-center gap-2">
                    <MapPin className="size-3 text-primary" /> Primary HQ / Node
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Global HQ or Cloud Infrastructure Region" className={inputBaseClasses} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="dropoff_location" render={({
            field
          }) => <FormItem className="col-span-1 md:col-span-2">
                  <FormLabel className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/70 flex items-center gap-2">
                    <MapPin className="size-3 text-primary" /> Deployment Region
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Secondary nodes or target markets" className={inputBaseClasses} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="vehicle_type" rules={{
            required: "Service type is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/70 flex items-center gap-2">
                    <Car className="size-3 text-primary" /> Service Architecture
                  </FormLabel>
                  <FormControl>
                    <select className={`${inputBaseClasses} appearance-none bg-transparent cursor-pointer font-default`} {...field}>
                      <option value="" disabled className="bg-background text-muted-foreground">Select Architecture</option>
                      <option value="AI Infrastructure" className="bg-card text-foreground">AI Compute Infrastructure</option>
                      <option value="Apple Business" className="bg-card text-foreground">Apple Business Innovation</option>
                      <option value="Salesforce SI" className="bg-card text-foreground">Salesforce SI/ISV/OEM</option>
                      <option value="Custom API" className="bg-card text-foreground">API & Systems Engineering</option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="passenger_count" rules={{
            required: "Stakeholder count is required",
            min: 1
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/70 flex items-center gap-2">
                    <Users className="size-3 text-primary" /> Stakeholders
                  </FormLabel>
                  <FormControl>
                    <Input type="number" min="1" placeholder="10" className={inputBaseClasses} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {}
            <FormField control={control} name="special_requests" render={({
            field
          }) => <FormItem className="col-span-1 md:col-span-2">
                  <FormLabel className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/70">Project Specifications</FormLabel>
                  <FormControl>
                    <textarea placeholder="Detail your requirements for OceanClaw, OceanWallet, or custom solution engineering..." className={`${inputBaseClasses} min-h-[80px] resize-y py-2 font-default`} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>

          {submitError && <div className="mt-6 p-4 bg-destructive/10 border border-destructive/20 rounded-sm">
              <p className="text-destructive text-sm font-default">{submitError}</p>
            </div>}

          <div className="mt-10">
            <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground font-default font-bold text-lg tracking-[0.1em] uppercase transition-all shadow-lg shadow-primary/20">
              {isSubmitting ? <>
                  <Loader className="animate-spin mr-2 size-5" />
                  Deploying...
                </> : 'Deploy Request'}
            </Button>
          </div>
        </Form>
      </CardContent>
    </Card>;
}
export default function ReserveYourJourney() {
  return <section data-section-id="149654" id="engineering-the-future" className="dark bg-background text-foreground relative min-h-screen flex items-center py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1633370/img/houston_dark_map_bg.png)] bg-cover bg-center opacity-20 mix-blend-luminosity grayscale" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background" />
        
        {}
        {}
        <div className="absolute top-[25%] left-[35%] hidden md:block">
          <div className="relative flex items-center justify-center">
            <div className="absolute size-12 bg-primary/10 rounded-full animate-ping" />
            <div className="absolute size-6 bg-primary/20 rounded-full animate-pulse" />
            <div className="relative size-2 bg-primary rounded-full shadow-[0_0_10px_rgba(34,211,238,0.8)]" />
            <span className="absolute left-6 text-[10px] font-mono text-primary/60 whitespace-nowrap tracking-widest">COMPUTE_01</span>
          </div>
        </div>

        {}
        <div className="absolute top-[50%] left-[45%] hidden md:block">
          <div className="relative flex items-center justify-center">
            <div className="absolute size-16 bg-primary/10 rounded-full animate-ping" style={{
            animationDelay: '0.5s'
          }} />
            <div className="absolute size-8 bg-primary/20 rounded-full animate-pulse" style={{
            animationDelay: '0.5s'
          }} />
            <div className="relative size-2.5 bg-primary rounded-full shadow-[0_0_15px_rgba(34,211,238,1)]" />
            <span className="absolute left-8 text-[10px] font-mono text-primary/60 whitespace-nowrap tracking-widest">CORE_INTELLIGENCE</span>
          </div>
        </div>

        {}
        <div className="absolute top-[65%] left-[52%] hidden md:block">
          <div className="relative flex items-center justify-center">
            <div className="absolute size-10 bg-primary/10 rounded-full animate-ping" style={{
            animationDelay: '1s'
          }} />
            <div className="absolute size-5 bg-primary/20 rounded-full animate-pulse" style={{
            animationDelay: '1s'
          }} />
            <div className="relative size-1.5 bg-primary rounded-full shadow-[0_0_8px_rgba(34,211,238,0.8)]" />
            <span className="absolute left-5 text-[10px] font-mono text-primary/60 whitespace-nowrap tracking-widest">GATEWAY_NODE</span>
          </div>
        </div>
      </div>

      {}
      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {}
          <div className="lg:col-span-5 space-y-6 text-center lg:text-left">
            <h2 className="text-4xl md:text-5xl lg:text-7xl font-default font-bold leading-[0.9] text-foreground tracking-tighter uppercase">
              Engineering <br className="hidden lg:block" />
              the <span className="text-primary">Future?</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-md mx-auto lg:mx-0 font-default leading-relaxed">
              Build scalable digital ecosystems with enterprise-grade execution. From Apple Business innovation to Salesforce architecture, we architect what's next.
            </p>
            <div className="pt-4 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-6 text-[10px] font-mono text-muted-foreground/60 tracking-widest uppercase">
              <div className="flex items-center gap-2">
                <div className="size-1 bg-primary rounded-full" />
                Sovereign Systems
              </div>
              <div className="flex items-center gap-2">
                <div className="size-1 bg-primary rounded-full" />
                Scalable Compute
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <FormProvider formKey="future_engineering_form_intelligence" formId="future_engineering_form_intelligence" sectionName="Reserve Your Journey" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            pickup_date: "",
            pickup_time: "",
            pickup_location: "",
            dropoff_location: "",
            vehicle_type: "",
            passenger_count: 1,
            special_requests: ""
          }} submitText="Deploy Request" successMessage="Your engineering request has been logged. Our technical team will reach out via secure channel to discuss integration.">
              <ReservationFormInner />
            </FormProvider>
          </div>

        </div>
      </div>
    </section>;
}